import { Account, TicketsAware } from "@services/account/types";
import { SnmpEntry } from "@services/catalog/types";
import { Indexed, ListPage } from "@services/common/types";
import { LocationEntity } from "@services/locations/types";
import {
  MonitoringTriggersStatus,
  PauseMonitoringStatus,
  Room,
} from "@services/rooms/types";

export interface DeviceCounters {
  locationsTotal?: number;
  roomsTotal?: number;
  roomsManaged?: number;
  devicesTotal?: number;
  devicesUnreachable?: number;
  devicesLoginError?: number;
  devicesApiError?: number;
  devicesInCall?: number;
  devicesMonitoringStalled?: number;
  devicesError?: number;
  devicesMonitoringPaused?: number;
  locationsError?: number;
  roomsError?: number;
}

export interface MonitoredDeviceListOfDevice extends ListPage<Device> {
  counters?: RoomCounters;
}

export interface RoomCounters {
  devicesApiError: number;
  devicesInCall: number;
  devicesLoginError: number;
  devicesMonitoringStalled: number;
  devicesTotal: number;
  devicesUnreachable: number;
  locationsTotal: number;
  roomsManaged: number;
  roomsTotal: number;
  devicesError: number;
  devicesMonitoringPaused: number;
  locationsError: number;
  roomsError: number;
}

export type IndexedDevice = Device & Indexed;
export type IndexedDevicesMeta = Pick<
  MonitoredDeviceListOfDevice,
  "size" | "total" | "index"
>;

export interface Device extends TicketsAware {
  detailedStatuses?: Array<DeviceStatus>;
  sipRegistrar?: string;
  monitoringLevel?: MonitoringLevel;
  provisioned?: boolean;
  thirdPartyId?: string;
  model?: string;
  protocol?: CodecProtocol;
  manufacturer?: string;
  logo?: string;
  room?: Room;
  type?: string;
  active?: boolean;
  macAddress?: string;
  apiSupported?: boolean;
  e164?: string;
  normalizedMacAddress?: string;
  isAggregator?: boolean;
  ucPlatform?: string;
  operatingSystem?: string;
  modelLogoId?: number;
  softwareVersion?: string;
  aggregator?: Device;
  logoId?: number;
  name?: string;
  vip?: boolean;
  snmpCommunity?: string;
  msTeamsId?: string;
  customerCostCenterId?: string;
  localIpAddress?: string;
  ipAddress?: string;
  callingMethod?: CallingMethod;
  gatekeeperIp?: string;
  serialNumber?: string;
  id?: string;
  status?: DeviceStatus;
  sipUri?: string;
  monitoringSources?: MonitoringSource[];
  category?: string;
  details?: DeviceDetails;
  callCapable?: boolean;
  externalId?: string;
  pingProtocol?: PingProtocol;
  apiStatusIcon?: DeviceStatusIcon;
  linkStatusIcon?: DeviceStatusIcon;
  loginStatusIcon?: DeviceStatusIcon;
  tags?: string[];
}

export interface DeviceDetails {
  assetTag?: string;
  isdnDialOutSupported?: boolean;
  skipAutoSelection?: boolean;
  isdnPrefix?: string;
  h323Id?: string;
  adapterPropertyMap?: { [key: string]: string };
  lastUpdatedDate?: number;
  extraProperties?: Array<AdvancedControllableProperty>;
  device?: Device;
  h323DialInPrefix?: string;
  leaseExpirationDate?: number;
  position?: number;
  createdDate?: number;
  customerAssetTag?: string;
  sipDialInPrefix?: string;
  warrantyExpirationDate?: number;
  totalPorts?: number;
  audioBridgeDialOutSupported?: boolean;
  isdnDialIn?: string;
  isdnSuffix?: string;
  skipManualSelection?: boolean;
  managementUrl?: string;
  sipDialInDomain?: string;
  producerNotes?: string;
  managementInterfaces?: Array<ManagementInterface>;
  directDialInPrefix?: string;
  customizedPropertyValues?: { [key: string]: string };
  deviceId?: string;
}

export interface DeviceComment {
  producerNotes?: string;
}

export interface DeviceStatus {
  lastUpdated?: number;
  historicalPropertiesDate?: number;
  inCall?: boolean;
  accountId?: string;
  reachable?: boolean;
  muted?: boolean;
  extendedProperties?: { [key: string]: string };
  historicalProperties?: { [key: string]: string };
  loginError?: string;
  h323Registered?: boolean;
  monitoringSource?: MonitoringSource;
  deviceVersion?: string;
  stale?: boolean;
  id?: DeviceStatusId;
  pingLatency?: number;
  snmpData?: Array<SnmpEntry>;
  pauseMonitoringStatus?: PauseMonitoringStatus;
  statistics?: Array<Statistics>;
  sipRegistered?: boolean;
  deviceId?: string;
  apiError?: string;
  meetingInstanceSummary?: MeetingInstanceSummary;
  apiUnknown?: boolean;
  loginUnknown?: boolean;
  monitoringTriggersStatus?: MonitoringTriggersStatus;
}

export interface ControllableType {
  "@class": string;
  label?: string; //Button
  labelPressed?: string;
  gracePeriod?: number;
  labelOff?: string; //Switch
  labelOn?: string;
  rangeEnd?: number; //Slider
  rangeStart?: number;
  labelEnd?: string;
  labelStart?: string;
  labels?: string[]; //Preset & DropDown
  options?: string[];
}

export interface AdvancedControllableProperty {
  name?: string;
  type?: ControllableType;
  timestamp?: number;
  value?: any;
  position?: number;
  monitoringSource?: MonitoringSource;
  icon?: string;
  favorite?: boolean;
  displayname?: string;
  group?: string;
}

export interface ManagementInterface {
  secure?: boolean;
  username?: string;
  remotePort?: number;
  password?: string; //If an account has turned on enableDeviceSecurity property, backend doesn't return passwords so it could be undefined
  protocol?: ManagementProtocol;
  remoteAddress?: string;
  deviceDetails?: DeviceDetails;
  id?: ManagementInterfaceId;
  localAddress?: string;
  encryptedPassword?: string;
  localPort?: number;
}

export interface ManagementInformationInterface {
  username?: string;
  password?: string;
  localUrl?: string;
  remoteUrl?: string;
}

export interface ManagementInterfaceId {
  protocol?: string;
  deviceId?: string;
}

export interface DeviceStatusId {
  timeStamp?: number;
  deviceId?: string;
}

export interface MeetingInstanceSummary {
  meetingTitle?: string;
  scheduledMeetingId?: number;
  instanceId?: number;
}

export interface Statistics {
  videoMcuPorts?: PortStats;
  audioMcuPorts?: PortStats;
  memoryInUse?: number;
  memoryTotal?: number;
  "@class": string;
  audioChannelStats?: AudioChannelStats;
  callStats?: CallStats;
  registrationStatus?: RegistrationStats;
  videoChannelStats?: VideoChannelStats;
  contentChannelStats?: ContentChannelStats;
  inCall?: boolean;
  cpuPercentage?: number;
  upTime?: number;
  numberOfProcesses?: number;
  networkIn?: number;
  networkOut?: number;
}

export interface PortStats {
  free?: number;
  occupied?: number;
  portUtilization?: number;
  reserved?: number;
  total?: number;
}

export interface AudioChannelStats {
  bitRateRx?: number;
  bitRateTx?: number;
  codec?: string;
  jitterRx?: number;
  jitterTx?: number;
  muteTx?: boolean;
  packetLossRx?: number;
  packetLossTx?: number;
}

export interface CallStats {
  callId?: string;
  callRateRx?: number;
  callRateTx?: number;
  percentPacketLossRx?: number;
  percentPacketLossTx?: number;
  protocol?: string;
  remoteAddress?: string;
  requestedCallRate?: number;
  totalPacketLossRx?: number;
  totalPacketLossTx?: number;
}

export interface RegistrationStats {
  h323Gatekeeper?: string;
  h323Registered?: boolean;
  sipDetails?: string;
  sipRegistered?: boolean;
  sipRegistrar?: string;
}

export interface VideoChannelStats {
  bitRateRx?: number;
  bitRateTx?: number;
  codec?: string;
  frameRateRx?: number;
  frameRateTx?: number;
  frameSizeRx?: string;
  frameSizeRxHeight?: number;
  frameSizeRxWidth?: number;
  frameSizeTx?: string;
  frameSizeTxHeight?: number;
  frameSizeTxWidth?: number;
  jitterRx?: number;
  jitterTx?: number;
  packetLossRx?: number;
  packetLossTx?: number;
}

export interface ContentChannelStats {
  bitRateRx?: number;
  bitRateTx?: number;
  codec?: string;
  frameRateRx?: number;
  frameRateTx?: number;
  frameSizeRx?: string;
  frameSizeRxHeight?: number;
  frameSizeRxWidth?: number;
  frameSizeTx?: string;
  frameSizeTxHeight?: number;
  frameSizeTxWidth?: number;
  jitterRx?: number;
  jitterTx?: number;
  packetLossRx?: number;
  packetLossTx?: number;
  percentPacketLossRx?: number;
  percentPacketLossTx?: number;
}

export enum MonitoringLevel {
  None = "None",
  Ping = "Ping",
  All = "All",
}

export enum XmlNs0CodecProtocol {
  H323Only = <any>"H323_Only",
  SIPOnly = <any>"SIP_Only",
  DualH323AndSIP = <any>"Dual_H323_And_SIP",
  ISDN = <any>"ISDN",
  MSTEAMS = <any>"MS_TEAMS",
  None = <any>"None",
}

export enum CodecProtocol {
  DualH323SIP = "Dual H.323 & SIP",
  H323Only = "H.323 Only",
  SIPOnly = "SIP Only",
  ISDN = "ISDN",
  MSTeams = "MS Teams",
  None = "None",
}

export const CodecProtocolLabels: { [k in CodecProtocol]: string } = {
  [CodecProtocol.DualH323SIP]: "Dual H.323 & SIP",
  [CodecProtocol.H323Only]: "H.323 Only",
  [CodecProtocol.SIPOnly]: "SIP Only",
  [CodecProtocol.ISDN]: "ISDN",
  [CodecProtocol.MSTeams]: "MS Teams",
  [CodecProtocol.None]: "None",
};

export enum CallingMethod {
  Auto = "Auto",
  H323E164 = "H.323 E.164",
  SIPURI = "SIP URI",
  H323IP = "H.323 IP",
  SIPIP = "SIP IP",
  ISDNNumber = "ISDN Number",
  MSTeamsID = "MS Teams ID",
  None = "None",
}

export const CallingMethodLabels: { [k in CallingMethod]: string } = {
  [CallingMethod.Auto]: "Auto",
  [CallingMethod.H323E164]: "E.164",
  [CallingMethod.SIPURI]: "SIP URI",
  [CallingMethod.H323IP]: "IP Address via H.323",
  [CallingMethod.SIPIP]: "IP Address via SIP",
  [CallingMethod.ISDNNumber]: "ISDN Number",
  [CallingMethod.MSTeamsID]: "MS Teams ID",
  [CallingMethod.None]: "Never call this device",
};

export enum MonitoringSource {
  CommProxy = "CommProxy",
  RoomController = "Room Controller",
  _3rdPartyManagement = "3rd Party Management",
  AVISPLMonitoringAgent = "AVI-SPL Monitoring Agent",
}

export enum ManagementProtocol {
  SSH = "SSH",
  TELNET = "TELNET",
  HTTP = "HTTP",
}

export type DevicesCount = number;
export interface DeviceFilters {
  accountId?: string[];
  id?: string[];
  vip?: boolean;
  ["details.warrantyExpirationDate.after"]?: number;
  ["details.warrantyExpirationDate.before"]?: number;
  ["details.leaseExpirationDate.after"]?: number;
  ["details.leaseExpirationDate.before"]?: number;
  ["room.id"]?: string[];
  ["room.location.id"]?: string;
  ["room.location.country"]?: string;
  ["room.location.managed"]?: boolean;
  ["room.location.name"]?: string;
  ["room.details.contactEmail"]?: string;
  ["room.name"]?: string;
  monitoringLevel?: DevicesServiceFilter[] | string;
  monitoringSources?: string[];
  manufacturer?: string;
  model?: string;
  name?: string;
  softwareVersion?: string;
  serialNumber?: string;
  ipAddress?: string;
  gatekeeperIp?: string;
  e164?: string;
  macAddress?: string;
  category?: string[];
  tags?: string[];
  type?: string[];
  active?: boolean;
  deviceType?: string[];
  ["status.deviceVersion"]?: boolean;
  ["status.inCall"]?: boolean;
  ["status.reachable"]?: boolean;
  ["status.stale"]?: boolean;
  ["status.apiError"]?: boolean;
  ["status.loginError"]?: boolean;
  ["status.pauseMonitoringStatus.paused"]?: boolean;
  ["status.linkUnknown"]?: boolean;
  ["status.monitoringTriggersStatus.paused"]?: boolean;
  isAggregator?: boolean;
  ["aggregator.id"]?: Device["id"];
  ucPlatform?: string;
  operatingSystem?: string;
  ["details.assetTag"]?: string;
  provisioned?: boolean;
  sipUri?: string;
  watchList?: boolean;
  "room.details.roomType"?: string[];
  "room.exchangeId"?: string;
  "room.managed"?: boolean;
  "room.details.roomNumber"?: string;
  "room.location.city"?: string;
  excludeSimpleAssets?: boolean;
  callCapable?: boolean;
  inACall?: boolean;
}

export const deviceUCPlatforms = [
  { label: "Legacy", value: "Legacy" },
  { label: "Google Workspace", value: "Google Workspace" },
  { label: "Microsoft Teams", value: "Microsoft Teams" },
  { label: "Webex by Cisco", value: "Webex by Cisco" },
  { label: "Zoom", value: "Zoom" },
];

export const deviceOperatingSystems = [
  { label: "Android", value: "Android" },
  { label: "Linux", value: "Linux" },
  { label: "Windows", value: "Windows" },
  { label: "Other", value: "Other" },
];

export type Controls = { [key: string]: any };

function hasSpecificStatistics(device: Device, classname: string) {
  if (device) {
    if (device.monitoringLevel == MonitoringLevel.All) {
      if (device && device.detailedStatuses) {
        for (var i = 0; i < device.detailedStatuses.length; i++) {
          let stats = device.detailedStatuses[i].statistics;
          if (stats) {
            for (var j = 0; j < stats.length; j++) {
              if (stats[j]["@class"] == classname) {
                return true;
              }
            }
          }
        }
      }
    }
  }
  return false;
}
export enum StatisticsClassNames {
  Endpoint = "com.avispl.symphony.api.dal.dto.monitor.EndpointStatistics",
  Mcu = "com.avispl.symphony.api.dal.dto.monitor.McuStatistics",
  Generic = "com.avispl.symphony.api.dal.dto.monitor.GenericStatistics",
}
export function hasEndpointStatistics(device: Device | undefined) {
  if (device) {
    if (device.monitoringLevel == MonitoringLevel.All) {
      if (device && device.detailedStatuses) {
        for (var i = 0; i < device.detailedStatuses.length; i++) {
          if (
            device.detailedStatuses[i].monitoringSource !==
            MonitoringSource.AVISPLMonitoringAgent
          ) {
            let stats = device.detailedStatuses[i].statistics;
            if (stats) {
              for (var j = 0; j < stats.length; j++) {
                if (stats[j]["@class"] == StatisticsClassNames.Endpoint) {
                  return true;
                }
              }
            }
          }
        }
      }
    }
  }
  return false;
}
export function hasMcuStatistics(device: Device | undefined) {
  return hasSpecificStatistics(device ?? {}, StatisticsClassNames.Mcu);
}
export function hasGenericStatistics(device: Device | undefined) {
  return hasSpecificStatistics(device ?? {}, StatisticsClassNames.Generic);
}

export function getProductName(
  device: Device | undefined,
  bFullname?: boolean
) {
  var productname = "";
  if (device) {
    if (device.name && device.name != "") {
      if (bFullname) {
        if (device.room) {
          productname = device.room.name ?? "";
          if (device.ipAddress && device.ipAddress != "") {
            productname += " (" + device.ipAddress + ")";
          } else productname += " " + getProductName(device, false);
        } else {
          productname = getProductName(device, false);
        }
      } else {
        productname = device.name;
      }
    } else {
      if (bFullname) {
        if (device.room) {
          productname = device.room.name ?? "";
          if (device.ipAddress && device.ipAddress != "") {
            productname += " (" + device.ipAddress + ")";
          } else productname += " " + getProductName(device, false);
        } else {
          productname = getProductName(device, false);
        }
      } else {
        if (device.manufacturer) {
          productname = device.manufacturer + " ";
          if (device.model) {
            productname += device.model;
          }
        } else {
          if (device.model) {
            productname = device.model;
          }
        }
      }
    }
  }
  return productname;
}

export type ProxyDevice = {
  accountId?: string;
  roomLocationId?: string;
  category?: string;
  isAggregator: boolean;
  type?: string;
};

export interface ArchivedDeviceLocation
  extends Pick<LocationEntity, "id" | "name"> {
  account: Pick<
    Account,
    | "id"
    | "name"
    | "active"
    | "totalFilesSize"
    | "numFiles"
    | "filesSizeLimit"
    | "recordingEnabled"
    | "commProxyEnabled"
  >;
}

export interface AchivedDeviceRoom
  extends Pick<Room, "id" | "name" | "inService" | "managed"> {
  location: ArchivedDeviceLocation;
}

export type ArchivedDeviceStatus = Pick<
  DeviceStatus,
  "apiUnknown" | "loginUnknown"
>;

export type ArchivedDevice = Pick<
  Device,
  | "id"
  | "name"
  | "customerCostCenterId"
  | "localIpAddress"
  | "serialNumber"
  | "macAddress"
  | "softwareVersion"
  | "e164"
  | "sipRegistrar"
  | "sipUri"
  | "protocol"
  | "active"
  | "vip"
  | "type"
  | "category"
  | "manufacturer"
  | "model"
  | "provisioned"
> &
  DeviceComment & {
    room: AchivedDeviceRoom;
    deletedTime?: number;
    deletedBy?: string;
    reason?: string;
    status: ArchivedDeviceStatus;
  };

export enum ArchivedDeviceReason {
  ReplacedUnderWarranty = "Replaced under warranty",
  ReplacedOutOfWarranty = "Replaced out of warranty",
  NoLongerRequired = "No longer required",
  Decommission = "Decommission",
  Other = "Other",
}

export enum ArchivedTimeValue {
  All = 0,
  Seven = 7,
  Thirty = 30,
  Hundred = 100,
  Year = 365,
}

export const archivedDeviceTimeFilter = [
  { label: "All", value: ArchivedTimeValue.All },
  { label: "Last 7 days", value: ArchivedTimeValue.Seven },
  { label: "Last 30 days", value: ArchivedTimeValue.Thirty },
  { label: "Last 100 days", value: ArchivedTimeValue.Hundred },
  { label: "Last 365 days", value: ArchivedTimeValue.Year },
];

export type ArchivedDeviceFilters = Partial<
  Omit<DeviceFilters, "room.location.id">
> & {
  "room.location.id"?: (string | undefined)[];
  days?: number;
};

export type PingProtocol = "ICMP" | "TCP";
export const pingProtocolValues: Filter<PingProtocol> = [
  { label: "ICMP", value: "ICMP" },
  { label: "TCP", value: "TCP" },
];

export type DeviceStatusIcon =
  | "ERROR"
  | "UNKNOWN"
  | "STALE"
  | "PAUSED"
  | "HIDDEN"
  | "SUCCESS";

export type BulkUpdateDeviceControlsPayload = {
  deviceId: string;
  controls: AdvancedControllableProperty[];
};
